/**
 * Zustand store for the Illustrator → Figma import flow.
 *
 * State is intentionally fine-grained so components can subscribe to only the
 * slice they render — e.g. high-frequency `progress` updates re-render just the
 * status panel, not the dropzone, mode picker, or header.
 *
 * The MuPDF Web Worker and the plugin postMessage bridge are managed here as
 * module-level singletons (created once, no React lifecycle), so there's no
 * effect churn and the plugin handshake is wired up the moment the UI loads.
 */

import { create } from 'zustand';
import { conformsToImporterFlow } from '@figma-importers/ui/contract';
import type {
  EditorType,
  ImportMode,
  PluginMessage,
  UIMessage,
} from '../types/messages';
import { PROTOCOL_VERSION } from '../types/messages';
import type { WorkerRequest, WorkerResponse } from '../lib/worker-messages';
import { validateAiFile } from '../lib/validate';
import { DEFAULT_VARIANT, variantById, type Variant } from '../variants';
import { createLicensing, type Gate, type ImportKind, type LicenseLinkName, type LicenseUser } from '@figma-importers/licensing';
import { identify, trackUsage } from '../telemetry';

/**
 * The free-tier gate and the licence, shared with every tomake.design importer that
 * charges — map-to-design is free and deliberately has none:
 * five imports a month here, Pro everywhere once a licence is known for the
 * signed-in Figma user (see @figma-importers/licensing).
 */
/*
 * BETA: FREE, AND NOT METERED. No gate, no monthly count, nothing to buy.
 *
 * The beta is over: this plugin charges on the same terms as the rest of the family. A free
 * editable or vector import places `FREE_PAGES_PER_IMPORT` artboards and counts against the
 * monthly allowance shared with every other tomake.design plugin; image mode stays free and
 * uncounted, as it is everywhere else.
 *
 * It ran as `beta: true`, which made the gate allow everything and told the server a mode it
 * does not count. Anyone who bought Pro during the beta keeps exactly what they had — the
 * plan was always read before the beta flag, so their licence never depended on it.
 */
export const licensing = createLicensing({
  app: DEFAULT_VARIANT.key,
  productName: DEFAULT_VARIANT.name,
});

/**
 * How the licence server classifies a mode.
 *
 * Only an editable import spends the monthly allowance; placing the page as a picture is free
 * on every plan, so 'image' must not be counted and must never be refused for being the sixth.
 * 'text' is editable — it produces real text layers, which is the thing being paid for.
 */
const kindOf = (mode: ImportMode): ImportKind => (mode === 'image' ? 'image' : 'editable');

export type ImportStatus =
  | 'idle'
  | 'converting'
  | 'placing'
  | 'done'
  | 'error';

export interface ImportProgress {
  completed: number;
  total: number;
}

interface ImportState {
  // Connection
  isReady: boolean;
  editorType: EditorType | null;
  /**
   * Which of the two published plugins is on the other end of the bridge. One
   * UI deploy serves both, so this is only known once the handshake names a
   * plugin id; until then the UI is on the "Connecting…" screen anyway and the
   * default variant supplies the branding.
   */
  variant: Variant;
  /**
   * Set when the UI↔plugin handshake never completes — the bridge is dead, so
   * the UI shows a recoverable error instead of an indefinite "Connecting…".
   */
  connectionError: string | null;
  /**
   * Set when the plugin handshakes but reports a different (or missing) protocol
   * version — the locally-loaded `plugin/code.js` is out of date relative to
   * this (auto-deployed) UI. Surfaced so the user knows to reload the plugin.
   */
  skewMessage: string | null;
  /** The signed-in Figma user, from the handshake; the licence follows this id. */
  user: LicenseUser | null;
  /** The free-tier gate as it stands; refreshed whenever the plan or the count changes. */
  gate: Gate;
  /** True when the last Import press was refused by the gate. */
  gateBlocked: boolean;
  // User inputs
  file: File | null;
  mode: ImportMode;
  /** Flatten transparency/effects via Ghostscript before converting. */
  flatten: boolean;
  /**
   * Raster scale for High-res image mode (1–4×). 3× ≈ retina/print quality.
   * The engine clamps it down so the longest edge stays ≤ Figma's 4096 px.
   */
  scale: number;
  notice: string | null;
  /**
   * Which artboards to import.
   *
   *   'first'  just artboard 1 — what a free plan allows, and the default when the file
   *            has more artboards than the plan covers
   *   'all'    every one of them
   *   'range'  the ones named in `range`
   */
  selection: 'first' | 'all' | 'range';
  /** A page range like "1-3, 7", read when `selection` is 'range'. */
  range: string;
  /** 'row' = side by side, 'column' = stacked. */
  layout: 'row' | 'column' | 'grid';
  /**
   * What the file says about itself, read before any import: artboard count, sizes, and a
   * thumbnail of the first. Null until the probe answers, and stays null if it fails — the
   * preview is an extra and its absence must not block the import.
   */
  probe: {
    pageCount: number;
    sizes: readonly { readonly width: number; readonly height: number }[];
    thumbnails: readonly Uint8Array[];
    /** Font families the document's text uses — what "Match fonts" offers to remap. */
    fonts: readonly string[];
  } | null;
  /** Every family Figma can set. Empty until the sandbox answers `get-fonts`. */
  availableFonts: readonly string[];
  /** Source family -> chosen Figma family. Absent keys match automatically. */
  fontMappings: Record<string, string>;
  // Progress / outcome
  status: ImportStatus;
  progress: ImportProgress | null;
  message: string | null;
  result: { count: number; skipped: number } | null;
  // Actions
  /** Ask the sandbox to open a licence link — buying Pro, from the panel. */
  openLicenseLink: (name: LicenseLinkName) => void;
  setFile: (file: File | null) => void;
  setMode: (mode: ImportMode) => void;
  setFlatten: (flatten: boolean) => void;
  setScale: (scale: number) => void;
  setSelection: (selection: 'first' | 'all' | 'range') => void;
  setRange: (range: string) => void;
  setLayout: (layout: 'row' | 'column' | 'grid') => void;
  setFontMapping: (from: string, to: string | null) => void;
  startImport: () => void;
  cancel: () => void;
  reset: () => void;
  /** Re-attempt the plugin handshake after a connection error. */
  retryConnection: () => void;
}

// ============================================
// Plugin bridge
// ============================================

function sendToPlugin(message: UIMessage): void {
  parent.postMessage({ pluginMessage: message }, '*');
}

// ============================================
// Worker singleton
// ============================================

let worker: Worker | null = null;
let jobId = 0;
let pending: { mode: ImportMode; fileName: string } | null = null;
/**
 * When the current import started, for the `ms` on its outcome event. Kept
 * beside `pending` rather than in the store: it's telemetry bookkeeping, and
 * putting it in state would re-render every subscriber for a number nothing
 * renders.
 */
let importStartedAt = 0;
/** Whether this window has already counted its `session`. See the ready handler. */
let sessionTracked = false;
// Artboards that failed to convert in the worker (carried into the final
// success message so a partial import is honest about what was dropped).
let conversionFailed = 0;
// Artboards actually forwarded to the plugin this job (0 ⇒ nothing converted).
let streamedCount = 0;

// Safety net: the plugin sandbox should reply to every 'create-nodes' message
// (import-success or error). If a reply is ever dropped, this keeps the UI from
// hanging on "Placing layers…" forever. Generous so it never pre-empts a large
// but still-progressing placement.
const PLACING_TIMEOUT_MS = 45_000;
let placingWatchdog: ReturnType<typeof setTimeout> | null = null;
// Set when the plugin acknowledges 'create-begin'. Lets the watchdog tell a
// dead UI↔plugin bridge (no ack) apart from a payload the sandbox choked on
// (ack received, but no placement progress).
let beginAcked = false;

// Conversion runs in the MuPDF worker; if the worker dies at load or its WASM
// init throws, it may never post back — leaving the UI stuck on "Converting…".
// Generous because a large multi-page file legitimately takes a while.
const CONVERTING_TIMEOUT_MS = 90_000;
let convertingWatchdog: ReturnType<typeof setTimeout> | null = null;

// The plugin sends 'ready' on startup and in reply to 'ui-ready'. If neither
// arrives, the bridge is dead and the UI would otherwise show "Connecting…"
// forever. We re-ask a few times, then surface a recoverable connection error.
const HANDSHAKE_RETRY_MS = 2_000;
const HANDSHAKE_MAX_RETRIES = 4;
let handshakeWatchdog: ReturnType<typeof setTimeout> | null = null;
let handshakeAttempts = 0;

function clearPlacingWatchdog(): void {
  if (placingWatchdog !== null) {
    clearTimeout(placingWatchdog);
    placingWatchdog = null;
  }
}

function clearConvertingWatchdog(): void {
  if (convertingWatchdog !== null) {
    clearTimeout(convertingWatchdog);
    convertingWatchdog = null;
  }
}

function armConvertingWatchdog(): void {
  clearConvertingWatchdog();
  convertingWatchdog = setTimeout(() => {
    convertingWatchdog = null;
    if (useImportStore.getState().status !== 'converting') return;
    pending = null;
    useImportStore.setState({
      status: 'error',
      progress: null,
      message:
        'Converting timed out — the PDF engine (MuPDF/WASM) didn’t respond. ' +
        'Close and reopen the plugin and try again; if it persists, the file may be too large.',
    });
  }, CONVERTING_TIMEOUT_MS);
}

function clearHandshakeWatchdog(): void {
  if (handshakeWatchdog !== null) {
    clearTimeout(handshakeWatchdog);
    handshakeWatchdog = null;
  }
}

/**
 * Drive the connection handshake: ask the plugin to (re)send 'ready', then wait.
 * Retries a few times to ride out the nested-iframe load, and finally surfaces a
 * recoverable connection error instead of an indefinite spinner.
 */
function armHandshakeWatchdog(): void {
  clearHandshakeWatchdog();
  sendToPlugin({ type: 'ui-ready', protocolVersion: PROTOCOL_VERSION });
  handshakeWatchdog = setTimeout(() => {
    handshakeWatchdog = null;
    if (useImportStore.getState().isReady) return;
    if (handshakeAttempts < HANDSHAKE_MAX_RETRIES) {
      handshakeAttempts += 1;
      armHandshakeWatchdog();
      return;
    }
    useImportStore.setState({
      connectionError:
        'Couldn’t reach the Figma plugin sandbox — the UI↔plugin bridge isn’t ' +
        'connected. Close and reopen the plugin. If it persists, reload/re-import ' +
        'the plugin (its sandbox code may need updating).',
    });
  }, HANDSHAKE_RETRY_MS);
}

function armPlacingWatchdog(): void {
  clearPlacingWatchdog();
  placingWatchdog = setTimeout(() => {
    placingWatchdog = null;
    const { status, progress } = useImportStore.getState();
    if (status !== 'placing') return;
    pending = null;
    // Three distinct failures, told apart by the begin-ack + progress count:
    //   • no ack          → the import request never round-tripped to the
    //                        plugin: a UI↔plugin bridge problem, not the file.
    //   • ack, 0 progress → the request arrived but the first artboard's
    //                        payload choked the sandbox (too large/complex).
    //   • ack, k progress → it placed k, then stalled partway.
    let message: string;
    if (!beginAcked) {
      message =
        'The plugin isn’t receiving the import request — this looks like a Figma UI↔plugin connection problem, not your file. Close and reopen the plugin, then try again.';
    } else if (!progress || progress.completed === 0) {
      message =
        'Figma received the request but couldn’t place the first artboard — it’s likely too large or complex. Try High-res image mode, or a simpler file.';
    } else {
      message = `Figma stopped responding after placing ${progress.completed} of ${progress.total} artboards. Please try again.`;
    }
    useImportStore.setState({ status: 'error', progress: null, message });
  }, PLACING_TIMEOUT_MS);
}

function failConversion(message: string): void {
  clearConvertingWatchdog();
  pending = null;
  if (useImportStore.getState().status === 'converting') {
    useImportStore.setState({ status: 'error', progress: null, message });
  }
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('../lib/mupdf.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) =>
      handleWorkerMessage(event.data);
    // Without these, a worker that dies at load or whose WASM init throws fails
    // silently — the UI would sit on "Converting…" with no error. Surface it.
    worker.onerror = (event: ErrorEvent) => {
      failConversion(
        `The PDF engine failed to load: ${event.message || 'worker error'}. ` +
          'Close and reopen the plugin and try again.',
      );
    };
    worker.onmessageerror = () => {
      failConversion('The PDF engine sent an unreadable response. Please try again.');
    };
  }
  return worker;
}

/*
 * Probe requests are numbered separately from import jobs: they are started by picking a
 * file rather than by importing one, and a stale answer must be ignorable on its own.
 */
/**
 * "1-3, 7" as zero-based indices, clamped to what the file actually holds.
 *
 * Anything unparseable is skipped rather than rejected: this is typed into a box while the
 * user is still editing it, so "1-" on the way to "1-3" must not throw. An empty result is
 * handled by the caller, which falls back to importing everything.
 */
/**
 * How many artboards the current choice would place.
 *
 * Shared by the gate and the footer so they cannot disagree: a button that offers an import
 * the gate is about to refuse is worse than either of them being wrong alone.
 */
export function selectedCount(state: {
  selection: 'first' | 'all' | 'range';
  range: string;
  probe: { pageCount: number } | null;
}): number {
  const count = state.probe?.pageCount ?? 0;
  if (count === 0) return 0;
  if (state.selection === 'first') return 1;
  if (state.selection === 'range') return parseRange(state.range, count).length;
  return count;
}

export function parseRange(text: string, pageCount: number): number[] {
  const picked = new Set<number>();
  for (const part of text.split(',')) {
    const piece = part.trim();
    if (!piece) continue;
    const dash = piece.indexOf('-');
    if (dash > 0) {
      const from = Number(piece.slice(0, dash).trim());
      const to = Number(piece.slice(dash + 1).trim());
      if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
      for (let n = Math.min(from, to); n <= Math.max(from, to); n++) {
        if (n >= 1 && n <= pageCount) picked.add(n - 1);
      }
    } else {
      const n = Number(piece);
      if (Number.isFinite(n) && n >= 1 && n <= pageCount) picked.add(n - 1);
    }
  }
  return [...picked].sort((a, b) => a - b);
}

let probeJob = 0;

function postToWorker(
  request: WorkerRequest,
  transfer: Transferable[] = [],
): void {
  getWorker().postMessage(request, transfer);
}

// ============================================
// Store
// ============================================

export const useImportStore = create<ImportState>((set, get) => ({
  isReady: false,
  editorType: null,
  variant: DEFAULT_VARIANT,
  connectionError: null,
  skewMessage: null,
  user: null,
  gate: licensing.gate(),
  gateBlocked: false,
  file: null,
  probe: null,
  availableFonts: [],
  fontMappings: {},
  selection: 'all',
  range: '',
  layout: 'row',
  /*
   * VECTOR BY DEFAULT. Editable paths are what someone opening an Illustrator or EPS file in
   * Figma came for; arriving at a flat picture and having to discover a toggle to get the
   * actual product is the wrong first run.
   *
   * It defaulted to image because `createNodeFromSvg` is synchronous and a pathological
   * artboard can stall the sandbox. That risk is real and is NOT removed by this line — what
   * covers it is the placing watchdog, which catches a stall and reports it rather than
   * leaving Figma frozen.
   *
   * The engine also offers a size guard, `maxVectorSvgBytes`, which rasterises oversized SVGs
   * and is reported in the UI as "N too complex, imported as image". It is deliberately still
   * off: it was written for the case where the user explicitly asked for editable layers, and
   * silently handing them an image instead is worse than a slow import. If stalls turn out to
   * be common now that this is the default rather than a choice, passing
   * `MAX_VECTOR_SVG_BYTES` is the lever, and the UI already explains the result.
   */
  mode: 'vector',
  flatten: false,
  // 3× ≈ retina/print quality — the documented default for image mode.
  scale: 3,
  notice: null,
  status: 'idle',
  progress: null,
  message: null,
  result: null,

  openLicenseLink: (name) => sendToPlugin({ type: 'open-external', link: name }),

  setFile: (file) => {
    if (!file) {
      set({ file: null, probe: null, fontMappings: {}, notice: null });
      return;
    }
    const error = validateAiFile(file);
    if (error) {
      set({ file: null, probe: null, fontMappings: {}, notice: error });
      return;
    }
    set({
      file,
      probe: null,
      // Mappings belong to the document they were chosen for.
      fontMappings: {},
      notice: null,
      status: 'idle',
      progress: null,
      message: null,
      result: null,
    });
    // Warm up the worker's WASM while the user picks a mode.
    postToWorker({ type: 'warmup' });

    /*
     * Read the file's shape while the user is still choosing how to import it, so the
     * settings screen can show the artboards instead of asking about a file it has not
     * opened. A separate read of the File: `convert` transfers its buffer, and a transferred
     * buffer cannot be read twice.
     *
     * `probeJob` guards against a second file picked before the first answers — a late reply
     * for a file no longer chosen is dropped rather than shown against the new one.
     */
    const job = ++probeJob;
    void file
      .arrayBuffer()
      .then((data) => {
        if (job !== probeJob) return;
        postToWorker({ type: 'probe', id: job, data, thumbnails: 24, fonts: true }, [data]);
      })
      .catch(() => undefined);
  },

  setMode: (mode) => {
    /*
     * Re-pick the default selection when the mode changes what it costs.
     *
     * The default is chosen when the probe lands, and it has to be: a free plan opening a
     * three-artboard file should land on the one it can place. But image mode is free
     * whatever the count, so arriving there from a gated mode would otherwise leave
     * "First artboard" selected with nothing charging for the other two — a restriction
     * the plan is not asking for.
     *
     * Only the default moves. A range the user typed is theirs and is left alone.
     */
    const { selection, probe } = get();
    const count = probe?.pageCount ?? 0;
    const limit = licensing.gate().pageLimit;
    const gatedHere = mode !== 'image' && Number.isFinite(limit) && count > limit;
    const nextSelection =
      selection === 'range'
        ? selection
        : gatedHere
          ? 'first'
          : selection === 'first' && count > 0
            ? 'all'
            : selection;
    set({ mode, selection: nextSelection });
  },

  setFlatten: (flatten) => set({ flatten }),

  setScale: (scale) => set({ scale }),

  setSelection: (selection) => set({ selection }),
  setRange: (range) => set({ range, selection: 'range' }),
  setLayout: (layout) => set({ layout }),

  setFontMapping: (from, to) => {
    const next = { ...get().fontMappings };
    // Null is "match it automatically" — the absence of a key, not a key meaning nothing.
    if (to) next[from] = to;
    else delete next[from];
    set({ fontMappings: next });
  },

  startImport: () => {
    const { file, mode, flatten: flattenWanted, scale, status } = get();

    /*
     * Flattening is Pro. Checked HERE as well as in the control, because the control's
     * disabled state only governs what can be clicked now — a licence that lapses between
     * turning it on and pressing Import would otherwise send `flatten: true` from a plan
     * that no longer includes it. `plan === 'pro'` and not "anything but free": beta is
     * unlimited for everything else, and this is the one thing it does not cover.
     */
    const flatten = licensing.gate().plan === 'pro' && flattenWanted;
    if (!file || status === 'converting' || status === 'placing') return;

    /*
     * The monthly allowance AND the page limit, both before any work.
     *
     * The page limit used to be checked when the converter reported its count, which was the
     * only honest moment while nothing had opened the file first. The probe opens it now, so
     * the number of artboards is known while the user is still choosing — and refusing then
     * is the difference between a settings screen that says what an import will cost and one
     * that takes the choice back after the button is pressed.
     *
     * The check at `begin` stays: this one depends on a probe that may not have answered.
     */
    const gate = licensing.gate({ kind: kindOf(mode), pages: selectedCount(get()) });
    if (!gate.allowed) {
      set({ gate, gateBlocked: true, notice: gate.reason ?? null });
      /*
       * The footer says "Unlock with Pro" in this state, so pressing it opens the checkout.
       * A button that names an action and then only writes a sentence somewhere is the
       * thing the label promised not to do.
       */
      get().openLicenseLink('checkout');
      return;
    }
    set({ gateBlocked: false });

    const id = (jobId += 1);
    pending = { mode, fileName: file.name };
    importStartedAt = Date.now();
    trackUsage('import-start', { mode, editor: get().editorType });
    set({
      status: 'converting',
      progress: { completed: 0, total: 0 },
      message: null,
      result: null,
    });
    // Guard the conversion phase too: a worker/WASM stall would otherwise hang
    // here with no error (the placing watchdog only covers the placement phase).
    armConvertingWatchdog();

    file
      .arrayBuffer()
      .then((data) => {
        /*
         * Resolved here rather than stored, because it depends on the probe: "first" is
         * artboard 0, "all" is everything (sent as undefined, which is also what a file
         * whose shape is unknown gets), and a range is whatever parses. An empty or
         * unparsed range imports everything rather than nothing — refusing to import a
         * file because a text box says "1-" would be a worse answer than ignoring it.
         */
        const state = useImportStore.getState();
        const count = state.probe?.pageCount ?? 0;
        const pages =
          state.selection === 'first'
            ? [0]
            : state.selection === 'range' && count > 0
              ? parseRange(state.range, count)
              : [];
        postToWorker(
          { type: 'convert', id, data, mode, flatten, scale, ...(pages.length ? { pages } : {}) },
          [data],
        );
      })
      .catch((error: unknown) => {
        clearConvertingWatchdog();
        pending = null;
        set({
          status: 'error',
          progress: null,
          message:
            error instanceof Error ? error.message : 'Could not read the file.',
        });
      });
  },

  cancel: () => {
    clearPlacingWatchdog();
    clearConvertingWatchdog();
    postToWorker({ type: 'cancel', id: jobId });
  },

  reset: () => {
    clearPlacingWatchdog();
    clearConvertingWatchdog();
    set({
      status: 'idle',
      progress: null,
      message: null,
      result: null,
      notice: null,
    });
  },

  retryConnection: () => {
    handshakeAttempts = 0;
    set({ connectionError: null });
    armHandshakeWatchdog();
  },
}));

// ============================================
// Message handlers (update the store from outside React)
// ============================================

function handleWorkerMessage(message: WorkerResponse): void {
  /*
   * Probe replies are answered first, because the guard below filters on the IMPORT job id
   * and a probe is not an import — it is numbered on its own counter and started by picking
   * a file rather than by importing one. Left below the guard, every preview was dropped
   * unless the two counters happened to coincide.
   */
  if (message.type === 'probed') {
    // A probe for a file that is no longer the chosen one is not an answer about anything.
    if (message.id !== probeJob) return;
    /*
     * Default to the artboards the current plan can actually place.
     *
     * A three-artboard file on a free plan defaults to "Every artboard (3)" only to refuse
     * it a moment later. pdf picks the free option in the same situation; the Pro rows stay
     * visible and one click away, which is what makes them an offer rather than a wall.
     *
     * Only when the plan is short: a Pro licence, or a file within the free allowance, is
     * left on "all" where it belongs.
     */
    const limit = licensing.gate().pageLimit;
    const overLimit = Number.isFinite(limit) && message.pageCount > limit;
    useImportStore.setState({
      probe: {
        pageCount: message.pageCount,
        sizes: message.sizes,
        thumbnails: message.thumbnails,
        fonts: message.fonts,
      },
      ...(overLimit ? { selection: 'first' as const } : {}),
    });
    /*
     * Ask for Figma's font list only when this document has fonts to remap. It is slow
     * enough to be felt, and an import of pure artwork never opens the control.
     */
    if (message.fonts.length > 0 && useImportStore.getState().availableFonts.length === 0) {
      sendToPlugin({ type: 'get-fonts' });
    }
    return;
  }
  if (message.type === 'probe-failed') {
    // The preview is an extra. The file may still import perfectly well, so this leaves the
    // settings screen without a rail rather than putting an error in front of the user.
    console.error('Preview unavailable:', message.message);
    return;
  }

  if (message.id !== jobId) return; // ignore superseded jobs

  switch (message.type) {
    case 'begin': {
      // Conversion has started and we know the count — open the placement stream
      // to the plugin now, so artboards are placed as they convert (pipelined)
      // rather than after the whole file is done.
      clearConvertingWatchdog();
      if (!pending) return;

      /*
       * THE PAGE LIMIT, applied here because this is the only honest moment for it: before
       * this message the page count is unknown, and after it artboards are already going into
       * the document. Refusing half-placed work would be worse than not refusing at all.
       *
       * The worker is told to stop so the rest of the file is not converted for nothing, and
       * `jobId` moves on so anything already in flight is ignored.
       */
      const pageGate = licensing.gate({ kind: kindOf(pending.mode), pages: message.total });
      if (!pageGate.allowed) {
        postToWorker({ type: 'cancel', id: jobId });
        pending = null;
        jobId += 1;
        useImportStore.setState({
          status: 'idle',
          progress: null,
          message: null,
          gate: pageGate,
          gateBlocked: true,
        });
        return;
      }

      beginAcked = false;
      conversionFailed = 0;
      streamedCount = 0;
      try {
        sendToPlugin({
          type: 'create-begin',
          mode: pending.mode,
          fileName: pending.fileName,
          total: message.total,
          layout: useImportStore.getState().layout,
          fontMappings: useImportStore.getState().fontMappings,
        });
      } catch (error) {
        failPlacement(error);
        return;
      }
      useImportStore.setState({
        status: 'placing',
        progress: { completed: 0, total: message.total },
      });
      armPlacingWatchdog();
      break;
    }

    case 'artboard':
      if (!pending) return;
      // Forward each converted artboard to the plugin immediately. Re-arm the
      // placing watchdog: the worker producing an artboard proves the pipeline
      // is alive even if the plugin hasn't reported placement progress yet.
      armPlacingWatchdog();
      try {
        sendToPlugin({
          type: 'place-artboard',
          index: message.index,
          artboard: message.artboard,
        });
        streamedCount += 1;
      } catch (error) {
        failPlacement(error);
      }
      break;

    case 'done':
      if (!pending) return;
      conversionFailed = message.failed;
      // Nothing converted (every artboard failed) — report it directly rather
      // than asking the plugin to finalise an empty stream. The plugin's open
      // session is harmlessly replaced on the next import.
      if (streamedCount === 0) {
        clearPlacingWatchdog();
        pending = null;
          useImportStore.setState({
          status: 'error',
          progress: null,
          message:
            message.failed > 0
              ? `None of the ${message.failed} artboard${message.failed === 1 ? '' : 's'} could be converted. The file may be malformed or unsupported.`
              : 'No artboards were found in this file.',
        });
        break;
      }
      // All artboards converted+streamed. Close the placement stream; the plugin
      // finalises and replies with import-success (or error).
      try {
        sendToPlugin({ type: 'create-end' });
      } catch (error) {
        failPlacement(error);
      }
      break;

    case 'cancelled':
      // Stop. Any artboards already placed remain on the canvas (the plugin's
      // open session is harmlessly replaced on the next import); we don't try to
      // un-place them. Late 'artboard' messages are ignored via the null pending.
      clearConvertingWatchdog();
      clearPlacingWatchdog();
      trackUsage('import-cancelled', {
        mode: pending?.mode,
        ms: Date.now() - importStartedAt,
      });
      pending = null;
      useImportStore.setState({ status: 'idle', progress: null, message: null });
      break;

    case 'error':
      clearConvertingWatchdog();
      clearPlacingWatchdog();
      trackUsage('import-failed', {
        mode: pending?.mode,
        ms: Date.now() - importStartedAt,
      });
      pending = null;
      useImportStore.setState({
        status: 'error',
        progress: null,
        message: message.message,
      });
      break;
  }
}

/** Report a failure that occurred while streaming artboards to the plugin. */
function failPlacement(error: unknown): void {
  clearPlacingWatchdog();
  pending = null;
  useImportStore.setState({
    status: 'error',
    progress: null,
    message:
      error instanceof Error
        ? error.message
        : 'Could not hand the artboards to Figma.',
  });
}

function handlePluginMessage(message: PluginMessage): void {
  switch (message.type) {
    case 'ready': {
      // Only accept a handshake from one of OUR plugins. This deploy serves
      // both published variants, so the id also tells us which one we are —
      // it drives the title, the blurb and the accepted extensions below. A
      // 'ready' carrying an unknown (or missing) pluginId isn't ours: ignore it
      // and keep showing "Connecting to Figma…" rather than wiring up to the
      // wrong sandbox.
      const variant = variantById(message.pluginId);
      if (!variant) break;
      clearHandshakeWatchdog();
      // The handshake completed, so the bridge is alive. But if the sandbox
      // reports a different (or, for a pre-v3 code.js, missing) protocol version,
      // the locally-loaded plugin is out of date relative to this auto-deployed
      // UI — every import would silently no-op. Surface it instead of pretending
      // we're ready.
      if (message.protocolVersion !== PROTOCOL_VERSION) {
        useImportStore.setState({
          isReady: false,
          editorType: message.editorType,
          variant,
          connectionError: null,
          skewMessage:
            'This plugin’s code is out of date and can’t talk to the current ' +
            'importer. In Figma: Plugins → Development → right-click ' +
            `“${variant.name}” → Reload (or re-import the manifest), then ` +
            'reopen the plugin.',
        });
        break;
      }
      // Nothing is reported before this point: the handshake is the first thing
      // that says which of the two plugins this window is, and the server
      // refuses a row that can't say. A skewed plugin above doesn't get here,
      // which is right — its imports won't work, so its numbers would be noise.
      identify(variant.key, message.user ?? null);
      const user = message.user?.id ? { id: message.user.id, name: message.user.name } : null;
      // A licence bought in any tomake.design plugin shows up here without a key being typed again.
      void licensing.sync(user).then(() => useImportStore.setState({ gate: licensing.gate() }));
      useImportStore.setState({ user });
      // Once per window, not once per handshake. The sandbox sends 'ready' on
      // startup *and* in reply to every 'ui-ready', and the UI asks again on a
      // retry — so counting handshakes would inflate `sessions` by however many
      // round trips the bridge happened to need.
      if (!sessionTracked) {
        sessionTracked = true;
        trackUsage('session', { editor: message.editorType });
      }
      useImportStore.setState({
        isReady: true,
        editorType: message.editorType,
        variant,
        connectionError: null,
        skewMessage: null,
      });
      break;
    }

    case 'begin-ack':
      // Ignore late messages from a cancelled/superseded job — `pending` is
      // cleared on cancel/done, so a drained plugin backlog can't resurrect the
      // placing state (and a false "stopped responding" watchdog error).
      if (!pending) return;
      // The request reached the sandbox and the reply path works — keep the
      // watchdog alive while the plugin starts on the first artboard.
      beginAcked = true;
      armPlacingWatchdog();
      break;

    case 'place-progress':
      if (!pending) return;
      // The plugin is alive and placing — refresh progress and reset the
      // watchdog so it only fires on a genuine stall, not slow-but-progressing.
      armPlacingWatchdog();
      useImportStore.setState({
        status: 'placing',
        progress: {
          completed: message.placed + message.skipped,
          total: message.total,
        },
      });
      break;

    case 'fonts':
      useImportStore.setState({ availableFonts: message.families });
      return;

    case 'import-success': {
      if (!pending) return;
      clearPlacingWatchdog();
      licensing.recordImport(useImportStore.getState().user, Math.max(1, message.count), kindOf(pending.mode));
      trackUsage('import-complete', {
        mode: pending.mode,
        // Artboards placed, which is the unit this importer produces. A count,
        // never a name.
        layers: message.count,
        ms: Date.now() - importStartedAt,
      });
      pending = null;
      const rasterized = message.rasterized ?? 0;
      const textLayers = message.textLayers ?? 0;
      const failed = conversionFailed;
      conversionFailed = 0;
      useImportStore.setState({
        status: 'done',
        progress: null,
        result: { count: message.count, skipped: message.skipped },
        message:
          `Imported ${message.count} artboard${message.count === 1 ? '' : 's'}` +
          (message.skipped > 0 ? ` · ${message.skipped} skipped` : '') +
          (textLayers > 0 ? ` · ${textLayers} editable text layers` : '') +
          (rasterized > 0
            ? ` · ${rasterized} too complex for editable layers, imported as image`
            : '') +
          (failed > 0
            ? ` · ${failed} couldn’t be converted`
            : ''),
      });
      break;
    }

    case 'error':
      clearPlacingWatchdog();
      pending = null;
      useImportStore.setState({
        status: 'error',
        progress: null,
        message: message.message,
      });
      break;
  }
}

// ============================================
// One-time plugin handshake (runs when this module loads)
// ============================================

if (typeof window !== 'undefined') {
  window.addEventListener('message', (event: MessageEvent) => {
    const message = event.data?.pluginMessage as PluginMessage | undefined;
    if (message) handlePluginMessage(message);
  });
  // Ask the plugin to (re)send its ready state, avoiding a startup race, and arm
  // the handshake watchdog so a dead bridge surfaces an error rather than an
  // indefinite "Connecting to Figma…" spinner.
  armHandshakeWatchdog();
  // Eagerly warm the MuPDF/WASM engine (~10 MB) the moment the UI loads, so it's
  // ready before the user picks a file and clicks Import — the first conversion
  // then starts immediately instead of waiting on a cold WASM load.
  postToWorker({ type: 'warmup' });
}

// Compile-time guarantee: this store satisfies the shared ImporterFlow contract.
conformsToImporterFlow(useImportStore);

licensing.subscribe(() => useImportStore.setState({ gate: licensing.gate() }));
